// Mobile bottom navigation bar (< md only).
//
// Before this, the ONLY way to switch top-level sections on a phone was to
// open the hamburger drawer, hunt for the link, and tap it. This bar puts the
// five most-used destinations one tap away, matching the platform bottom-tab
// idiom (icon + label, active indicator, ≤5 items — Material / iOS HIG).
//
// Paket 2 (Redesign): Diese Datei hielt bis dahin eine EIGENE `TABS`-Liste mit
// eigenen Labels ("Dashboard"/"Aufgaben"/"Inbox"/"Mein Tag"), während die
// Sidebar dieselben Ziele "Dashboard"/"Meine Aufgaben"/"Inbox"/"Mein Tag"
// nannte — die Drift war auf einem Screenshot direkt sichtbar. Jetzt ist die
// Bar eine reine PROJEKTION von `mobileNav()` (lib/navigation.ts): Labels,
// Icons, Reihenfolge und Zähler-Zuordnung kommen aus dem NavTarget, es gibt
// hier keine zweite Definitionsstelle mehr. Nur die Form (56px Höhe,
// Safe-Area, Abzeichen, Aktivstrich oben) gehört dieser Datei.

import { Link, useLocation } from "wouter-preact";
import { inboxUnreadSig } from "../lib/inboxBadge";
import { isActivePath, mobileNav, type NavTarget } from "../lib/navigation";

/** Zähler-Auflösung: `NavTarget.badge` ist ein Bezeichner, kein Wert. */
function badgeCount(target: NavTarget): number {
  return target.badge === "inbox" ? inboxUnreadSig.value : 0;
}

export function MobileNav(): preact.JSX.Element {
  const [location] = useLocation();

  return (
    <nav
      class="md:hidden fixed bottom-0 inset-x-0 z-30 flex items-stretch border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]/95 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]"
      aria-label="Hauptnavigation"
    >
      {mobileNav().map((target) => {
        const active = isActivePath(target, location);
        const badge = badgeCount(target);
        const Icon = target.icon;
        return (
          <Link
            key={target.id}
            href={target.path}
            aria-current={active ? "page" : undefined}
            class={`relative flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] px-1 text-label font-medium transition-colors ${
              active
                ? "text-[color:var(--color-primary-strong)]"
                : "text-[color:var(--color-on-surface-variant)]"
            }`}
            title={target.label}
          >
            {active && (
              <span
                class="absolute top-0 h-0.5 w-8 rounded-full bg-[color:var(--color-primary)]"
                aria-hidden="true"
              />
            )}
            <span class="relative">
              <Icon size={20} aria-hidden="true" />
              {badge > 0 && (
                <span
                  class="absolute -top-1.5 -right-2 inline-flex min-w-[15px] h-[15px] items-center justify-center rounded-full bg-[color:var(--color-primary)] px-1 text-label font-semibold text-[color:var(--color-on-primary)]"
                  aria-label={`${badge} ungelesen`}
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
            <span class="truncate max-w-full leading-none">{target.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
